/**
 * Scherm 01 — Aanmelden via Microsoft Entra ID.
 *
 * De statische bestanden zijn vrij op te halen, de gegevens niet: `/api/*` staat op
 * `authenticated`. Een 401 van de API betekent dus simpelweg "nog niet aangemeld", en
 * dan komt dit scherm naar voren. Op `/login` staat het er altijd, ook als je al bent
 * aangemeld: dan biedt de kaart "Doorgaan naar de Houtstaat" en uitloggen aan.
 *
 * De achtergrond is de werkplaats van De Klerk Binnenbouw in 3D (`Werkplaats3D`): een
 * kar rijdt zijn route langs de stations, en linksonder licht het station op waar hij
 * nu is. Dat onderdeel brengt three.js mee en wordt daarom pas geladen als dit scherm
 * er staat; tot dan, en zonder WebGL, blijft de blauwe gradient van de huisstijl staan.
 */
import { lazy, Suspense, useEffect, useState, type CSSProperties } from 'react';

import logo from '../assets/dkb-logo-wit.svg';
import { ROUTE, stationskleur, type Stand } from '../onderdelen/werkplaatsroute';

const Werkplaats3D = lazy(() => import('../onderdelen/Werkplaats3D'));

const SERVICEDESK = `mailto:servicedesk@kdvs.nl?subject=${encodeURIComponent('Aanmelden Houtstaat')}`;

/** Breed genoeg voor de kaart naast de werkplaats; smaller staat de kaart eronder. */
const BREED = '(min-width: 960px)';

function useBreed(): boolean {
  const [breed, setBreed] = useState(() => window.matchMedia(BREED).matches);
  useEffect(() => {
    const mq = window.matchMedia(BREED);
    const kijk = () => setBreed(mq.matches);
    mq.addEventListener('change', kijk);
    return () => mq.removeEventListener('change', kijk);
  }, []);
  return breed;
}

interface Props {
  /** De naam van wie al is aangemeld (op `/login`), of niets. */
  aangemeldAls?: string | null;
}

export function Aanmelden({ aangemeldAls }: Props) {
  const breed = useBreed();
  const [stand, setStand] = useState<Stand | null>(null);
  const [scene, setScene] = useState<'laden' | 'klaar' | 'geen'>('laden');

  return (
    <div className={`hs-login hs-login--${scene}`}>
      <div className="hs-login__scene" aria-hidden>
        <Suspense fallback={null}>
          <Werkplaats3D
            onStand={setStand}
            onKlaar={(gelukt) => setScene(gelukt ? 'klaar' : 'geen')}
            ruimteRechts={breed ? 440 : 0}
          />
        </Suspense>
      </div>

      <header className="hs-login__merk">
        <img src={logo} alt="De Klerk Binnenbouw" className="hs-login__logo" />
        <p className="hs-annotatie">De Klerk Binnenbouw B.V.</p>
      </header>

      <div className="hs-login__verhaal">
        <h1 className="hs-login__titel">Houtstaat</h1>
        <ol className="hs-login__route" aria-label="De route van een kar door de werkplaats">
          {ROUTE.map((s) => (
            <li
              key={s}
              className={s === stand?.station ? (stand.rijdt ? 'volgend' : 'nu') : undefined}
              style={{ '--kleur': stationskleur(s) } as CSSProperties}
            >
              {s}
            </li>
          ))}
        </ol>
      </div>

      {/* Geen kaart eromheen: het aanmelden staat op de werkplaats zelf. */}
      <main className="hs-login__aanmelden">
        <p className="hs-annotatie hs-login__welkom">Welkom</p>
        <h2 className="hs-login__kop">Aanmelden</h2>
        {aangemeldAls ? (
          <>
            <p className="hs-login__uitleg">
              Je bent aangemeld als <strong>{aangemeldAls}</strong>.
            </p>
            <a className="hs-login__knop" href="/">
              Doorgaan naar de Houtstaat
            </a>
            <a className="hs-login__knop hs-login__knop--rand" href="/.auth/logout?post_logout_redirect_uri=/login">
              Uitloggen
            </a>
          </>
        ) : (
          <>
            <p className="hs-login__uitleg">Meld je aan met je KDVS-account.</p>
            <a className="hs-login__knop" href="/.auth/login/aad?post_login_redirect_uri=/">
              <MicrosoftLogo />
              Aanmelden met Microsoft
            </a>
          </>
        )}
        <p className="hs-login__hulp">
          Lukt aanmelden niet? <a href={SERVICEDESK}>Mail de servicedesk</a>.
        </p>
      </main>
    </div>
  );
}

/** De vier vlakken van Microsoft, zoals in de richtlijn voor "Aanmelden met Microsoft". */
function MicrosoftLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden>
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}
